import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { apiBaseUrl, prepareAuthHeaders } from "./apiBase";

// Marketing → Meta Ads → Product Sets (`/api/v1/marketing/meta/*`).

const unwrap = (res) => {
  if (!res) return null;
  if (typeof res === "object" && "success" in res) {
    return res.success ? res.data : null;
  }
  return res.data ?? res;
};

const EMPTY_OVERVIEW = { productSets: [], catalog: { lastMetaReadAt: null } };

export const marketingApi = createApi({
  reducerPath: "marketingApi",
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl,
    prepareHeaders: (headers) => prepareAuthHeaders(headers, { contentTypeJson: true }),
  }),
  tagTypes: ["MetaProductSet", "MetaProductSetList", "MetaPickerProducts"],
  keepUnusedDataFor: 60,

  endpoints: (builder) => ({
    fetchMetaProductSets: builder.query({
      query: () => "marketing/meta/product-sets",
      transformResponse: (res) => unwrap(res) || EMPTY_OVERVIEW,
      providesTags: [{ type: "MetaProductSetList", id: "LIST" }],
    }),

    fetchMetaProductSet: builder.query({
      query: (id) => `marketing/meta/product-sets/${id}`,
      transformResponse: unwrap,
      providesTags: (_result, _err, id) => [{ type: "MetaProductSet", id: Number(id) }],
    }),

    fetchMetaPickerProducts: builder.query({
      query: (params = {}) => ({ url: "marketing/meta/products", params }),
      transformResponse: unwrap,
      providesTags: [{ type: "MetaPickerProducts", id: "LIST" }],
    }),

    createMetaProductSet: builder.mutation({
      query: (body) => ({ url: "marketing/meta/product-sets", method: "POST", body }),
      transformResponse: unwrap,
      invalidatesTags: [{ type: "MetaProductSetList", id: "LIST" }],
    }),

    updateMetaProductSet: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `marketing/meta/product-sets/${id}`,
        method: "PATCH",
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: (_result, _err, arg) => [
        { type: "MetaProductSet", id: Number(arg?.id) },
        { type: "MetaProductSetList", id: "LIST" },
      ],
    }),

    deleteMetaProductSet: builder.mutation({
      query: (id) => ({ url: `marketing/meta/product-sets/${id}`, method: "DELETE" }),
      transformResponse: unwrap,
      invalidatesTags: [{ type: "MetaProductSetList", id: "LIST" }],
    }),

    replaceMetaProductSetProducts: builder.mutation({
      // A selection key is one model in one colour; every size is implied.
      query: ({ id, selectionKeys, expectedProductsUpdatedAt }) => ({
        url: `marketing/meta/product-sets/${id}/products`,
        method: "PUT",
        body: {
          selectionKeys,
          ...(expectedProductsUpdatedAt ? { expectedProductsUpdatedAt } : {}),
        },
      }),
      transformResponse: unwrap,
      // The page adopts the returned detail itself, so only the list refetches.
      invalidatesTags: [{ type: "MetaProductSetList", id: "LIST" }],
    }),

    includeMetaProductSetInCatalog: builder.mutation({
      query: (id) => ({
        url: `marketing/meta/product-sets/${id}/include-in-catalog`,
        method: "POST",
      }),
      transformResponse: unwrap,
      invalidatesTags: [
        { type: "MetaProductSetList", id: "LIST" },
        { type: "MetaPickerProducts", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useFetchMetaProductSetsQuery,
  useFetchMetaProductSetQuery,
  useFetchMetaPickerProductsQuery,
  useCreateMetaProductSetMutation,
  useUpdateMetaProductSetMutation,
  useDeleteMetaProductSetMutation,
  useReplaceMetaProductSetProductsMutation,
  useIncludeMetaProductSetInCatalogMutation,
} = marketingApi;
